import { useCallback, useMemo, useState } from 'react'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import {
  GetModuleAvailabilitiesDocument,
  GetStartedAiToursFeatureDocument,
  GetStartedCategoryOverrideDocument,
  GetStartedPoliciesDocument,
  GetStartedTaskCompletionDocument,
  GetStartedTaskOverrideDocument,
} from 'generated/resources/graphql'
import { Box, Text } from '@factorialco/deprecated-design-system'
import i18n from 'modules/core/lib/i18n'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Loading from 'modules/core/components/Loading'
import CategoryAccordion from 'modules/get_started/components/CategoryAccordion'
import { getCategories, CORE_CATEGORY_ID } from 'modules/get_started/lib/taskCatalog'
import { useEffect } from 'modules/core/hooks/useEffect'

const GetStartedPage = () => {
  const { company } = useCurrentSession()

  const { data: aiToursData, loading: aiToursLoading } = useQuery(
    GetStartedAiToursFeatureDocument,
    { variables: { companyIds: [company.id] } }
  )
  const aiToursEnabled =
    aiToursData?.features.featuresConnection.nodes.some(
      (f) => f.name === 'show_get_started_ai_tours' && f.enabled
    ) ?? false

  const allCategories = useMemo(() => getCategories(), [])
  const moduleIds = useMemo(
    () => [
      ...new Set(allCategories.map((c) => c.moduleName).filter((m): m is string => m !== null)),
    ],
    [allCategories]
  )
  const { data, loading: policiesLoading } = useQuery(GetStartedPoliciesDocument)
  const { data: taskCompletionData, loading: taskCompletionLoading } = useQuery(
    GetStartedTaskCompletionDocument,
    { fetchPolicy: 'cache-and-network' }
  )
  const { data: modulesData, loading: modulesLoading } = useQuery(GetModuleAvailabilitiesDocument, {
    variables: { ids: moduleIds },
  })
  const { data: overridesData, loading: overridesLoading } = useQuery(
    GetStartedCategoryOverrideDocument
  )
  const { data: taskOverridesData, loading: taskOverridesLoading } = useQuery(
    GetStartedTaskOverrideDocument
  )

  const isAuthorized = data?.getStarted._policies.showGetStartedOnboardingHub.can
  const isLoading =
    policiesLoading ||
    taskCompletionLoading ||
    modulesLoading ||
    overridesLoading ||
    taskOverridesLoading ||
    aiToursLoading

  const availableModules = useMemo(() => {
    const availabilities = modulesData?.companies.moduleAvailabilities ?? []
    return new Set(availabilities.filter((m) => m.available).map((m) => m.id))
  }, [modulesData])

  const overridesByCategory = useMemo(() => {
    const nodes =
      overridesData?.getStarted.categoryOverridesConnection.edges?.map((edge) => edge.node) ?? []
    return new Map(nodes.map((o) => [o.category, o.visible]))
  }, [overridesData])

  const hiddenTaskKeys = useMemo(() => {
    const nodes =
      taskOverridesData?.getStarted.taskOverridesConnection.edges?.map((edge) => edge.node) ?? []
    return new Set(nodes.filter((o) => !o.visible).map((o) => o.taskKey))
  }, [taskOverridesData])

  const filteredCategories = useMemo(
    () =>
      allCategories
        .map((c) => ({
          ...c,
          tasks: c.tasks.filter((t) => !hiddenTaskKeys.has(t.taskKey)),
        }))
        .filter((c) => c.id === CORE_CATEGORY_ID || c.tasks.length > 0),
    [allCategories, hiddenTaskKeys]
  )

  const subscribedCategories = useMemo(
    () =>
      filteredCategories.filter((c) => {
        const override = overridesByCategory.get(c.id)
        if (override !== undefined) return override
        return c.moduleName === null || availableModules.has(c.moduleName)
      }),
    [filteredCategories, availableModules, overridesByCategory]
  )

  const completedTaskKeys = useMemo(() => {
    const completions =
      taskCompletionData?.getStarted.taskCompletionsConnection.edges?.map((edge) => edge.node) ?? []
    return new Set(completions.map((c) => c.taskKey))
  }, [taskCompletionData])

  const coreCategory = useMemo(
    () => filteredCategories.find((c) => c.id === CORE_CATEGORY_ID),
    [filteredCategories]
  )
  const modulesCategories = useMemo(
    () =>
      filteredCategories.filter((c) => {
        if (c.id === CORE_CATEGORY_ID) return false
        const override = overridesByCategory.get(c.id)
        if (override !== undefined) return override
        return c.moduleName === null || availableModules.has(c.moduleName)
      }),
    [filteredCategories, availableModules, overridesByCategory]
  )

  const isCoreCompleted = useMemo(
    () =>
      coreCategory ? coreCategory.tasks.every((t) => completedTaskKeys.has(t.taskKey)) : false,
    [coreCategory, completedTaskKeys]
  )

  const firstIncompleteTaskKey = useMemo(() => {
    const orderedCategories = [...(coreCategory ? [coreCategory] : []), ...modulesCategories]
    for (const category of orderedCategories) {
      const incompleteTask = category.tasks.find((t) => !completedTaskKeys.has(t.taskKey))
      if (incompleteTask) return incompleteTask.taskKey
    }
    return null
  }, [coreCategory, modulesCategories, completedTaskKeys])

  const [expandedTaskKey, setExpandedTaskKey] = useState<string | null>(null)
  const [isExpandedTaskInitialized, setIsExpandedTaskInitialized] = useState(false)

  const handleTaskToggle = useCallback((taskKey: string) => {
    setExpandedTaskKey((prev) => (prev === taskKey ? null : taskKey))
  }, [])

  const { completedCategoryCount, progressPercent } = useMemo(() => {
    const completed = subscribedCategories.filter((category) =>
      category.tasks.every((t) => completedTaskKeys.has(t.taskKey))
    ).length
    const total = subscribedCategories.length
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0
    return { completedCategoryCount: completed, progressPercent: percent }
  }, [subscribedCategories, completedTaskKeys])

  useEffect(() => {
    if (!isLoading && !isExpandedTaskInitialized) {
      setIsExpandedTaskInitialized(true)
      setExpandedTaskKey(firstIncompleteTaskKey)
    }
  }, [isLoading, firstIncompleteTaskKey, isExpandedTaskInitialized])

  useEffect(() => {
    track(trackingEvents.getStarted.viewGetStartedPage)
  }, [])

  if (isLoading || !isExpandedTaskInitialized) return <Loading />

  return (
    <ResponsivePage.Body authorized={isAuthorized}>
      <Box
        gap="s16"
        maxWidth="narrowPage"
        width="full"
        paddingBottom="s96"
        style={{ margin: '0 auto' }}
      >
        <Box gap="s4">
          <Text.Normal size="300" weight="semibold" color="grey1000">
            {i18n.t('growth.get_started.title')}
          </Text.Normal>
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Box
              background="grey300"
              height="s6"
              borderRadius={{ all: 'abs004' }}
              style={{
                flex: '1',
                overflow: 'hidden',
              }}
            >
              <Box
                borderRadius={{ all: 'abs004' }}
                height="full"
                style={{
                  width: `${progressPercent}%`,
                  backgroundColor: '#06838B',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
            <Text.Normal size="200" color="grey700">
              {progressPercent}%
            </Text.Normal>
          </Box>
          <Text.Normal size="200" color="grey700">
            {i18n.t('growth.get_started.completed_modules', {
              completedCount: completedCategoryCount,
              totalCount: subscribedCategories.length,
            })}
          </Text.Normal>
        </Box>

        {coreCategory && (
          <CategoryAccordion
            category={coreCategory}
            completedTaskKeys={completedTaskKeys}
            defaultExpanded={!isCoreCompleted}
            expandedTaskKey={expandedTaskKey}
            onTaskToggle={handleTaskToggle}
            aiToursEnabled={aiToursEnabled}
          />
        )}

        {!isCoreCompleted && (
          <Box alignItems="center" padding="s4">
            <Text.Normal size="200" color="grey700">
              {i18n.t('growth.get_started.divider_text')}
            </Text.Normal>
          </Box>
        )}

        <Box gap="s12">
          {modulesCategories.map((category) => (
            <CategoryAccordion
              key={category.id}
              category={category}
              completedTaskKeys={completedTaskKeys}
              locked={!isCoreCompleted}
              expandedTaskKey={expandedTaskKey}
              onTaskToggle={handleTaskToggle}
              aiToursEnabled={aiToursEnabled}
            />
          ))}
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default GetStartedPage
